import { useState, useEffect } from 'react';
import {
  Layout,
  Card,
  Form,
  Input,
  Select,
  Button,
  Space,
  Typography,
  Row,
  Col,
  InputNumber,
  Switch,
  message,
  Checkbox,
  Table,
  Tooltip,
  Spin,
} from 'antd';
import AddOptionModal from './AddOptionModal';
import { ArrowLeftOutlined, SaveOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { testSchema } from '../schemas/testSchema';
import { useMsal } from '@azure/msal-react';
import useTemplateState from '../../../../../Template/atoms/variables';
import { useAssetCategories } from '../hooks/useAssetCategories';
import { createTest, getTests, updateTest, getInstrumentOptions } from '../services/test.service.ts';
import { useTestBuilder } from '../context/TestBuilderContext';

const { Header, Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

// Componente auxiliar para renderizar o campo variant condicionalmente
const VariantField = ({ stepIndex, fieldIndex, control, errors, mockData }) => {
  const fieldType = useWatch({
    control,
    name: `steps.${stepIndex}.fields.${fieldIndex}.type`,
  });

  // Só renderiza o campo variant se o tipo for 'select'
  if (fieldType !== 'select') {
    return null;
  }

  return (
    <Col span={4}>
      <Form.Item
        label='Variante'
        size='small'
        validateStatus={errors.steps?.[stepIndex]?.fields?.[fieldIndex]?.typeVariant ? 'error' : ''}
        help={errors.steps?.[stepIndex]?.fields?.[fieldIndex]?.typeVariant?.message}
        required
      >
        <Controller
          name={`steps.${stepIndex}.fields.${fieldIndex}.typeVariant`}
          control={control}
          render={({ field }) => (
            <Select {...field} style={{ height: '32px' }} placeholder='Variante'>
              {mockData.fieldVariants.map((variant) => (
                <Option key={variant.value} value={variant.value}>
                  {variant.label}
                </Option>
              ))}
            </Select>
          )}
        />
      </Form.Item>
    </Col>
  );
};

// Componente auxiliar para renderizar os controles de campo com span dinâmico
const FieldControls = ({ stepIndex, fieldIndex, control }) => {
  const fieldType = useWatch({
    control,
    name: `steps.${stepIndex}.fields.${fieldIndex}.type`,
  });

  // Se o tipo for 'select', o campo variant estará visível, então usa span 4
  // Caso contrário, usa span 8 para ocupar mais espaço
  const spanSize = fieldType === 'select' ? 4 : 8;

  return (
    <Col span={spanSize}>
      <Space size='large' style={{ width: '100%' }}>
        <Form.Item label='Obrigatório' size='small' style={{ margin: 0 }}>
          <Controller
            name={`steps.${stepIndex}.fields.${fieldIndex}.isRequired`}
            control={control}
            render={({ field }) => <Switch {...field} checked={field.value} size='small' />}
          />
        </Form.Item>
        <Form.Item label='Desabilitado' size='small' style={{ margin: 0 }}>
          <Controller
            name={`steps.${stepIndex}.fields.${fieldIndex}.isDisabled`}
            control={control}
            render={({ field }) => <Switch {...field} checked={field.value} size='small' />}
          />
        </Form.Item>
      </Space>
    </Col>
  );
};

// Componente auxiliar para renderizar os campos de configuração do step
const StepConfigFields = ({ stepIndex, control, errors, mockData, setValue }) => {
  const stepType = useWatch({
    control,
    name: `steps.${stepIndex}.stepType`,
  });

  return (
    <Row gutter={[16, 16]}>
      {/* Campo título - sempre presente */}
      <Col span={8}>
        <Controller
          name={`steps.${stepIndex}.title`}
          control={control}
          render={({ field }) => (
            <Form.Item
              label='Título do Step'
              validateStatus={errors.steps?.[stepIndex]?.title ? 'error' : ''}
              help={errors.steps?.[stepIndex]?.title?.message}
              required
            >
              <Input {...field} placeholder='Digite o título do step' />
            </Form.Item>
          )}
        />
      </Col>

      {/* Campo tipo - sempre visível */}
      <Col span={8}>
        <Controller
          name={`steps.${stepIndex}.stepType`}
          control={control}
          render={({ field }) => (
            <Form.Item
              label='Tipo do Step'
              validateStatus={errors.steps?.[stepIndex]?.stepType ? 'error' : ''}
              help={errors.steps?.[stepIndex]?.stepType?.message}
              required
            >
              <Select
                {...field}
                placeholder='Selecione o tipo'
                onChange={(value) => {
                  field.onChange(value);
                  if (value === 'camera' || value === 'evidence') {
                    // Quando trocar para camera ou evidence, garantir que tenha pelo menos 1 sub step
                    const currentSteps = control._formValues.steps || [];
                    const currentStep = currentSteps[stepIndex];
                    if (!currentStep?.subSteps || currentStep.subSteps.length === 0) {
                      const subStepBase = {
                        title: 'Sub Step 1',
                        hints: ['Hint 1'],
                        photo: '',
                        shouldValidate: false,
                      };

                      // Só adiciona instrumentId se for camera, não evidence
                      if (value === 'camera') {
                        subStepBase.instrumentId = [];
                      }

                      setValue(`steps.${stepIndex}.subSteps`, [subStepBase]);
                    }
                  } else if (value === 'form') {
                    // Quando trocar para form, garantir que tenha pelo menos 1 campo
                    const currentSteps = control._formValues.steps || [];
                    const currentStep = currentSteps[stepIndex];
                    if (!currentStep?.fields || currentStep.fields.length === 0) {
                      setValue(`steps.${stepIndex}.fields`, [
                        {
                          type: 'input',
                          typeVariant: 'single',
                          isDisabled: false,
                          isRequired: true,
                          label: 'Campo 1',
                          options: [],
                          selectedOptions: [],
                        },
                      ]);
                    }
                  } else if (value === 'table') {
                    // Quando trocar para table, inicializar configuração da tabela
                    setValue(`steps.${stepIndex}.tableConfig`, {
                      rowLabel: '',
                      rows: ['Linha 1'],
                      columns: ['Coluna 1'],
                    });
                  }
                }}
              >
                {mockData.stepTypes.map((type) => (
                  <Option key={type.value} value={type.value}>
                    {type.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
        />
      </Col>

      {/* Campo obrigatório - sempre presente */}
      <Col span={8}>
        <Space>
          <Controller
            name={`steps.${stepIndex}.isRequired`}
            control={control}
            render={({ field }) => (
              <Form.Item label='Obrigatório'>
                <Switch {...field} checked={field.value} />
              </Form.Item>
            )}
          />
        </Space>
      </Col>
    </Row>
  );
};

const CreateTestForm = ({ testId, existingData, isLoading }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { instance } = useMsal();
  const { setPendingApiCalls } = useTemplateState();
  const [loading, setLoading] = useState(false);
  const [basicInfoCompleted, setBasicInfoCompleted] = useState(false);
  const isEditMode = !!testId && testId !== 'create' && !!existingData;

  // Carregar categorias de assets
  const { categories: assetCategories, loading: categoriesLoading } = useAssetCategories(
    instance,
    setPendingApiCalls,
  );

  // Estado para carregar instrumentos
  const [instruments, setInstruments] = useState([]);
  const [instrumentsLoading, setInstrumentsLoading] = useState(false);


  // Contexto dos testes para atualizar a lista após criação
  const { setTests } = useTestBuilder();

  // Estado para controlar propriedades disponíveis baseadas nas categorias selecionadas
  const [availableProperties, setAvailableProperties] = useState([]);

  // Função para converter dados do teste existente para o formato do formulário
  const convertTestDataToFormData = (test) => {
    if (!test) return null;

    return {
      title: test.title || '',
      reportName: test.reportName || '',
      code: test.code || '',
      platform: test.platform || 'mobile',
      allowedAttempts: test.allowedAttempts || 1,
      assetCategory: test.assetCategories || test.assetCategory || [],
      instrumentIds: test.instrumentIds || [],
      annotations: test.annotations && test.annotations.length > 0 ? test.annotations : [''],
      assetDisplayProperties: test.assetDisplayProperties || [],
      steps: test.steps?.map((step) => {
        const convertedStep = {
          title: step.title || '',
          stepType:
            step.stepType === 'camera' && !step.subSteps?.[0]?.instrumentIds
              ? 'evidence'
              : step.stepType || 'form',
          isRequired: step.isRequired !== undefined ? step.isRequired : true,
          isHidden: step.isHidden || false,
        };

        if (step.stepType === 'form') {
          convertedStep.fields = step.fields?.map((field) => {
            const convertedField = {
              type: field.type || 'select',
              typeVariant: field.typeVariant || 'single',
              isDisabled: field.isDisabled || false,
              isRequired: field.isRequired !== undefined ? field.isRequired : true,
              label: field.label || '',
              options: field.options || [],
              selectedOptions: [],
            };

            // Determinar selectedOptions baseado no validValue
            if (field.validValue && field.options) {
              if (field.typeVariant === 'single' && typeof field.validValue === 'string') {
                const index = field.options.findIndex((opt) => opt === field.validValue);
                if (index !== -1) {
                  convertedField.selectedOptions = [index];
                }
              } else if (field.typeVariant === 'multiple' && Array.isArray(field.validValue)) {
                convertedField.selectedOptions = field.validValue
                  .map((val) => field.options.findIndex((opt) => opt === val))
                  .filter((idx) => idx !== -1);
              }
            }

            return convertedField;
          }) || [
            {
              type: 'select',
              typeVariant: 'single',
              isDisabled: false,
              isRequired: true,
              isHidden: true,
              defaultValue: '',
              selectedOptions: [],
              label: 'Campo 1',
              options: [],
            },
          ];
        } else if (step.stepType === 'camera' || convertedStep.stepType === 'evidence') {
          convertedStep.durationInSeconds = step.durationInSeconds;
          convertedStep.intervalInSeconds = step.intervalInSeconds;
          convertedStep.subSteps =
            step.subSteps?.map((subStep) => ({
              title: subStep.title || '',
              hints: subStep.hints || ['Hint 1'],
              photo: subStep.photo || '',
              instrumentId: subStep.instrumentIds || [],
              shouldValidate: subStep.shouldValidate || false,
            })) || [];
        } else if (step.stepType === 'table') {
          convertedStep.tableConfig = {
            rowLabel: step.rowLabel || '',
            rows: step.rows || ['Linha 1'],
            columns: step.columns || ['Coluna 1'],
          };
        }

        return convertedStep;
      }) || [
        {
          title: '',
          stepType: 'form',
          isRequired: true,
          isHidden: false,
          fields: [
            {
              type: 'select',
              typeVariant: 'single',
              isDisabled: false,
              isRequired: true,
              isHidden: true,
              defaultValue: '',
              selectedOptions: [],
              label: 'Campo 1',
              options: [],
            },
          ],
          subSteps: [],
        },
      ],
    };
  };

  // Função para obter propriedades baseadas nas categorias selecionadas
  const getPropertiesFromSelectedCategories = (selectedCategories) => {
    if (!selectedCategories || selectedCategories.length === 0) {
      return [];
    }

    const properties = new Set();
    selectedCategories.forEach((categoryId) => {
      const category = assetCategories.find((cat) => cat.id === categoryId);
      if (category && category.properties) {
        category.properties.forEach((prop) => {
          properties.add(JSON.stringify(prop)); // Use JSON para evitar duplicatas por referência
        });
      }
    });

    return Array.from(properties).map((prop) => JSON.parse(prop));
  };

  const defaultFormValues = {
    title: '',
    reportName: '',
    code: '',
    platform: 'mobile',
    allowedAttempts: 1,
    assetCategory: [],
    instrumentIds: [],
    annotations: [''],
    assetDisplayProperties: [],
    steps: [
      {
        title: '',
        stepType: 'form',
        isRequired: true,
        isHidden: false,
        fields: [
          {
            type: 'select',
            typeVariant: 'single',
            isDisabled: false,
            isRequired: true,
            isHidden: true,
            defaultValue: '',
            selectedOptions: [],
            label: 'Campo 1',
            options: [],
          },
        ],
        subSteps: [],
      },
    ],
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    getValues,
    reset,
  } = useForm({
    resolver: zodResolver(testSchema),
    defaultValues: defaultFormValues,
  });

  const watchedSteps = watch('steps');
  const watchedAssetCategory = watch('assetCategory');

  // Estado para controlar se os dados já foram carregados
  const [dataLoaded, setDataLoaded] = useState(false);

  // Carregar instrumentos na inicialização
  useEffect(() => {
    const fetchInstruments = async () => {
      setInstrumentsLoading(true);
      setPendingApiCalls((current) => current.concat(['getInstrumentOptions']));
      
      try {
        const data = await getInstrumentOptions(instance);
        setInstruments(data || []);
      } catch (error) {
        console.error('Error fetching instruments:', error);
        setInstruments([]);
      } finally {
        setInstrumentsLoading(false);
        setPendingApiCalls((current) => current.filter((item) => item !== 'getInstrumentOptions'));
      }
    };

    fetchInstruments();
  }, [instance, setPendingApiCalls]);

  // Carregar dados existentes quando em modo de edição
  useEffect(() => {
    if (isEditMode && existingData && !isLoading && assetCategories.length > 0 && !dataLoaded) {
      const formData = convertTestDataToFormData(existingData);
      if (formData) {
        // Primeiro, atualizar as propriedades disponíveis baseadas nas categorias carregadas
        if (formData.assetCategory && formData.assetCategory.length > 0) {
          const properties = getPropertiesFromSelectedCategories(formData.assetCategory);
          setAvailableProperties(properties);

          // Aguardar um tick para garantir que as propriedades foram atualizadas
          setTimeout(() => {
            reset(formData);
            setBasicInfoCompleted(true);
            setDataLoaded(true);
          }, 100);
        } else {
          reset(formData);
          setBasicInfoCompleted(true);
          setDataLoaded(true);
        }
      }
    }
  }, [isEditMode, existingData, isLoading, reset, assetCategories, dataLoaded]);

  // Atualizar propriedades disponíveis quando as categorias mudarem
  useEffect(() => {
    if (watchedAssetCategory && assetCategories.length > 0) {
      const properties = getPropertiesFromSelectedCategories(watchedAssetCategory);
      setAvailableProperties(properties);

      // Limpar propriedades selecionadas que não existem mais nas novas categorias
      const currentSelected = getValues('assetDisplayProperties') || [];
      const availableKeys = properties.map((p) => p.key);
      const validSelected = currentSelected.filter((key) => availableKeys.includes(key));

      if (validSelected.length !== currentSelected.length) {
        setValue('assetDisplayProperties', validSelected);
      }
    } else {
      setAvailableProperties([]);
      setValue('assetDisplayProperties', []);
    }
  }, [watchedAssetCategory, assetCategories]);

  // Dados mockados para os selects
  const mockData = {
    assetCategories: assetCategories.map((category) => ({
      value: category.id,
      label: category.name,
    })),
    assetDisplayProperties: availableProperties.map((prop) => ({
      value: prop.key,
      label: prop.label,
    })),
    stepTypes: [
      { value: 'form', label: 'Formulário' },
      { value: 'camera', label: 'Câmera' },
      { value: 'evidence', label: 'Evidência' },
      { value: 'table', label: 'Tabela/Matriz' },
    ],
    fieldTypes: [
      { value: 'select', label: 'Select' },
      { value: 'input', label: 'Input' },
    ],
    fieldVariants: [
      { value: 'single', label: 'Single' },
      { value: 'multiple', label: 'Multiple' },
    ],
    instruments: instruments,
  };

  // Lista das propriedades válidas de asset para identificar variáveis
  const assetPropertyNames = availableProperties.map((prop) => prop.key);

  // Função para validar se as informações básicas estão completas
  const validateBasicInfo = () => {
    const basicFields = [
      'title',
      'code',
      'reportName',
      'platform',
      'allowedAttempts',
      'assetCategory',
      'instrumentIds',
      'assetDisplayProperties',
    ];
    const values = getValues();

    return basicFields.every((field) => {
      const value = values[field];
      if (
        field === 'assetDisplayProperties' ||
        field === 'assetCategory' ||
        field === 'instrumentIds'
      ) {
        return Array.isArray(value) && value.length > 0;
      }
      if (field === 'allowedAttempts') {
        return typeof value === 'number' && value >= 1;
      }
      return value && value.toString().trim() !== '';
    });
  };

  // Observar mudanças nos campos básicos
  const watchedBasicFields = watch([
    'title',
    'code',
    'reportName',
    'platform',
    'allowedAttempts',
    'assetCategory',
    'instrumentIds',
    'annotations',
    'assetDisplayProperties',
  ]);

  useEffect(() => {
    // Em modo de edição com propriedades disponíveis, revalidar
    if (isEditMode && availableProperties.length > 0) {
      setBasicInfoCompleted(validateBasicInfo());
    } else if (!isEditMode) {
      setBasicInfoCompleted(validateBasicInfo());
    }
  }, [watchedBasicFields, availableProperties, isEditMode]);

  // Função para determinar se uma opção é uma variável (baseado se é uma propriedade válida)
  const isVariableOption = (option) => {
    if (!option) return false;
    // Remove o $ se existir para verificar se é uma variável válida
    const cleanOption = option.startsWith('$') ? option.slice(1) : option;
    return assetPropertyNames.includes(cleanOption);
  };

  // Função para obter o nome da propriedade (remove $ se existir para exibição)
  const getVariableFromOption = (option) => {
    if (!option) return option;
    return option.startsWith('$') ? option.slice(1) : option;
  };

  // Função para criar uma opção de variável (adiciona $ na frente se ainda não tiver)
  const createVariableOption = (variable) => {
    if (!variable) return variable;
    return variable.startsWith('$') ? variable : `$${variable}`;
  };

  // Estado para controlar o modal de seleção de tipo de opção
  const [optionTypeModal, setOptionTypeModal] = useState({
    visible: false,
    stepIndex: null,
    fieldIndex: null,
  });

  // Função para abrir o modal de seleção de tipo
  const openOptionTypeModal = (stepIndex, fieldIndex) => {
    setOptionTypeModal({
      visible: true,
      stepIndex,
      fieldIndex,
    });
  };

  // Função para adicionar opção baseada no tipo selecionado
  const addOptionByType = (type, value) => {
    const { stepIndex, fieldIndex } = optionTypeModal;

    if (stepIndex === null || fieldIndex === null) return;

    const currentSteps = getValues('steps');
    const updatedSteps = [...currentSteps];

    if (type === 'variable') {
      if (!value) return;
      updatedSteps[stepIndex].fields[fieldIndex].options.push(createVariableOption(value));
    }

    if (type === 'text') {
      if (!value || !value.trim()) return;
      updatedSteps[stepIndex].fields[fieldIndex].options.push(value.trim());
    }

    setValue('steps', updatedSteps);

    // Fechar o modal e limpar estados
    setOptionTypeModal({ visible: false, stepIndex: null, fieldIndex: null });
  };

  // Componente para renderizar uma opção simples
  const OptionField = ({
    stepIndex,
    fieldIndex,
    optionIndex,
    option,
    field,
    control,
    canRemove,
    onRemove,
  }) => {
    const isVariable = isVariableOption(option);

    return (
      <div
        key={optionIndex}
        style={{ display: 'flex', alignItems: 'center', marginBottom: 8, gap: 8 }}
      >
        <Checkbox
          checked={field.selectedOptions?.includes(optionIndex) || false}
          onChange={(e) =>
            handleOptionSelection(stepIndex, fieldIndex, optionIndex, e.target.checked)
          }
        />

        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              background: isVariable ? '#e6f7ff' : '#f6ffed',
              color: isVariable ? '#1890ff' : '#52c41a',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: '500',
            }}
          >
            {isVariable ? 'Variável' : 'Texto'}
          </span>
          <span style={{ flex: 1 }}>
            {isVariable
              ? availableProperties.find((prop) => prop.key === getVariableFromOption(option))
                  ?.label || getVariableFromOption(option)
              : option}
          </span>
        </div>

        {canRemove && (
          <Button
            type='text'
            danger
            size='small'
            onClick={() => onRemove(stepIndex, fieldIndex, optionIndex)}
            style={{ height: '32px' }}
          >
            Remover
          </Button>
        )}
      </div>
    );
  };

  const handleBack = () => {
    navigate('/settings/tests');
  };

  // Se está carregando dados existentes ou categorias, mostrar loading
  if (isEditMode && (isLoading || categoriesLoading)) {
    return (
      <Layout
        style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Spin size='large' tip={isLoading ? 'Carregando teste...' : 'Carregando categorias...'} />
      </Layout>
    );
  }

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      // Processar os dados para o formato correto
      const processedData = {
        title: data.title,
        reportName: data.reportName,
        code: data.code,
        platform: data.platform,
        allowedAttempts: data.allowedAttempts,
        assetCategories: data.assetCategory,
        instrumentIds: data.instrumentIds,
        annotations: data.annotations.filter((annotation) => annotation.trim() !== ''),
        assetDisplayProperties: data.assetDisplayProperties,
        steps: data.steps.map((step) => {
          const processedStep = {
            title: step.title,
            stepType: step.stepType,
            isRequired: step.isRequired,
            ...(step.isHidden && { isHidden: step.isHidden }),
            ...(step.durationInSeconds && { durationInSeconds: step.durationInSeconds }),
            ...(step.intervalInSeconds && { intervalInSeconds: step.intervalInSeconds }),
          };

          if (step.stepType === 'form') {
            processedStep.isHidden = false;
            processedStep.fields = step.fields.map((field) => {
              let defaultValue = '';

              // Processar defaultValue baseado nas opções selecionadas
              if (
                field.type === 'select' &&
                field.selectedOptions &&
                field.selectedOptions.length > 0
              ) {
                const selectedValues = field.selectedOptions
                  .map((optionIndex) => {
                    const option = field.options[optionIndex]?.trim();
                    if (!option) return null;
                    // Se for uma variável e não tiver $, adiciona o $
                    if (isVariableOption(option) && !option.startsWith('$')) {
                      return `$${option}`;
                    }
                    return option;
                  })
                  .filter(Boolean);

                if (field.typeVariant === 'single') {
                  defaultValue = selectedValues[0] || '';
                } else if (field.typeVariant === 'multiple') {
                  defaultValue = selectedValues;
                }
              }

              const processedField = {
                type: field.type,
                typeVariant: field.type === 'select' ? field.typeVariant : 'single',
                isDisabled: field.isDisabled,
                isRequired: field.isRequired,
                defaultValue: '',
                validValue: defaultValue,
                label: field.label,
              };

              // Adicionar opções apenas se for do tipo select
              if (field.type === 'select' && field.options) {
                processedField.options = field.options
                  .filter((option) => option.trim() !== '')
                  .map((option) => {
                    // Se for uma variável e não tiver $, adiciona o $
                    if (isVariableOption(option) && !option.startsWith('$')) {
                      return `$${option}`;
                    }
                    return option;
                  });
              }

              return processedField;
            });
          } else if (step.stepType === 'camera') {
            processedStep.subSteps = step.subSteps.map((subStep) => ({
              title: subStep.title,
              hints: subStep.hints.filter((hint) => hint.trim() !== ''),
              photo: subStep.photo || '',
              ...(subStep.instrumentId && { instrumentIds: subStep.instrumentId }),
              ...(subStep.shouldValidate && { shouldValidate: subStep.shouldValidate }),
            }));
          } else if (step.stepType === 'evidence') {
            // Para evidência, enviar como camera no payload mas sem instrumentIds
            processedStep.stepType = 'camera';
            processedStep.subSteps = step.subSteps.map((subStep) => ({
              title: subStep.title,
              hints: subStep.hints.filter((hint) => hint.trim() !== ''),
              photo: subStep.photo || '',
              shouldValidate: false, // Sempre false para evidência
              // Não incluir instrumentId para evidência
            }));
          } else if (step.stepType === 'table') {
            // Para steps de tabela, adicionar configuração da matriz
            processedStep.rowLabel = step.tableConfig?.rowLabel || '';
            processedStep.rows = step.tableConfig?.rows || [];
            processedStep.columns = step.tableConfig?.columns || [];
          }

          return processedStep;
        }),
      };

      // Chamada real para a API
      if (isEditMode) {
        setPendingApiCalls((current) => current.concat(['updateTest']));
        const result = await updateTest(instance, testId, processedData);
        message.success('Teste atualizado com sucesso!');
      } else {
        setPendingApiCalls((current) => current.concat(['createTest']));
        const result = await createTest(instance, processedData);
        message.success('Teste criado com sucesso!');
      }

      // Atualizar a lista de testes após criação/atualização bem-sucedida
      try {
        setPendingApiCalls((current) => current.concat(['getTests']));
        const updatedTests = await getTests(instance);
        setTests(updatedTests || []);
      } catch (error) {
        console.warn('Erro ao atualizar lista de testes:', error);
        // Não quebra o fluxo se houver erro na atualização da lista
      } finally {
        setPendingApiCalls((current) => current.filter((item) => item !== 'getTests'));
      }

      navigate('/settings/tests');
    } catch (error) {
      console.error(`Erro ao ${isEditMode ? 'atualizar' : 'criar'} teste:`, error);
      message.error(
        `Erro ao ${isEditMode ? 'atualizar' : 'criar'} teste: ${error.message || 'Erro desconhecido'}`,
      );
    } finally {
      setLoading(false);
      setPendingApiCalls((current) =>
        current.filter((item) => item !== (isEditMode ? 'updateTest' : 'createTest')),
      );
    }
  };

  const addStep = () => {
    const currentSteps = getValues('steps');
    setValue('steps', [
      ...currentSteps,
      {
        title: '',
        stepType: 'form',
        isRequired: true,
        isHidden: false,
        fields: [
          {
            type: 'select',
            typeVariant: 'single',
            isDisabled: false,
            isRequired: true,
            isHidden: true,
            defaultValue: '',
            selectedOptions: [],
            label: 'Campo 1',
            options: [],
          },
        ],
        subSteps: [],
      },
    ]);
  };

  const addField = (stepIndex) => {
    const currentSteps = getValues('steps');
    const updatedSteps = [...currentSteps];
    const fieldCount = updatedSteps[stepIndex].fields.length + 1;
    updatedSteps[stepIndex].fields.push({
      type: 'select',
      typeVariant: 'single',
      isDisabled: false,
      isRequired: true,
      isHidden: true,
      defaultValue: '',
      selectedOptions: [],
      label: `Campo ${fieldCount}`,
      options: [],
    });
    setValue('steps', updatedSteps);
  };

  const removeField = (stepIndex, fieldIndex) => {
    const currentSteps = getValues('steps');
    const updatedSteps = [...currentSteps];
    if (updatedSteps[stepIndex].fields.length > 1) {
      updatedSteps[stepIndex].fields.splice(fieldIndex, 1);
      setValue('steps', updatedSteps);
    }
  };

  const addSubStep = (stepIndex) => {
    const currentSteps = getValues('steps');
    const updatedSteps = [...currentSteps];
    const subStepCount = updatedSteps[stepIndex].subSteps.length + 1;
    const currentStep = updatedSteps[stepIndex];

    const newSubStep = {
      title: `Sub Step ${subStepCount}`,
      hints: ['Hint 1'],
      photo: '',
      shouldValidate: false,
    };

    // Só adiciona instrumentId se for camera, não evidence
    if (currentStep.stepType === 'camera') {
      newSubStep.instrumentId = [];
    }

    updatedSteps[stepIndex].subSteps.push(newSubStep);
    setValue('steps', updatedSteps);
  };

  const removeSubStep = (stepIndex, subStepIndex) => {
    const currentSteps = getValues('steps');
    const updatedSteps = [...currentSteps];
    if (updatedSteps[stepIndex].subSteps.length > 1) {
      updatedSteps[stepIndex].subSteps.splice(subStepIndex, 1);
      setValue('steps', updatedSteps);
    }
  };

  const addHint = (stepIndex, subStepIndex) => {
    const currentSteps = getValues('steps');
    const updatedSteps = [...currentSteps];
    updatedSteps[stepIndex].subSteps[subStepIndex].hints.push('');
    setValue('steps', updatedSteps);
  };

  const removeHint = (stepIndex, subStepIndex, hintIndex) => {
    const currentSteps = getValues('steps');
    const updatedSteps = [...currentSteps];
    updatedSteps[stepIndex].subSteps[subStepIndex].hints.splice(hintIndex, 1);
    setValue('steps', updatedSteps);
  };

  const addAnnotation = () => {
    const currentAnnotations = getValues('annotations');
    setValue('annotations', [...currentAnnotations, '']);
  };

  const removeAnnotation = (annotationIndex) => {
    const currentAnnotations = getValues('annotations');
    if (currentAnnotations.length > 1) {
      const updatedAnnotations = [...currentAnnotations];
      updatedAnnotations.splice(annotationIndex, 1);
      setValue('annotations', updatedAnnotations);
    }
  };

  const addOption = (stepIndex, fieldIndex) => {
    const currentSteps = getValues('steps');
    const updatedSteps = [...currentSteps];
    updatedSteps[stepIndex].fields[fieldIndex].options.push('');
    setValue('steps', updatedSteps);
  };

  const removeOption = (stepIndex, fieldIndex, optionIndex) => {
    const currentSteps = getValues('steps');
    const updatedSteps = [...currentSteps];
    if (updatedSteps[stepIndex].fields[fieldIndex].options.length > 1) {
      updatedSteps[stepIndex].fields[fieldIndex].options.splice(optionIndex, 1);
      setValue('steps', updatedSteps);
    }
  };

  const removeStep = (index) => {
    const currentSteps = getValues('steps');
    if (currentSteps.length > 1) {
      setValue(
        'steps',
        currentSteps.filter((_, i) => i !== index),
      );
    }
  };

  const handleOptionSelection = (stepIndex, fieldIndex, optionIndex, checked) => {
    const currentSteps = getValues('steps');
    const updatedSteps = [...currentSteps];
    const field = updatedSteps[stepIndex].fields[fieldIndex];

    if (!field.selectedOptions) {
      field.selectedOptions = [];
    }

    if (field.typeVariant === 'single') {
      // Para single select, só permite uma seleção
      if (checked) {
        field.selectedOptions = [optionIndex]; // Substitui qualquer seleção anterior
      } else {
        field.selectedOptions = [];
      }
    } else if (field.typeVariant === 'multiple') {
      // Para multiple select, permite múltiplas seleções
      if (checked) {
        if (!field.selectedOptions.includes(optionIndex)) {
          field.selectedOptions = [...field.selectedOptions, optionIndex];
        }
      } else {
        field.selectedOptions = field.selectedOptions.filter((index) => index !== optionIndex);
      }
    }

    setValue('steps', updatedSteps);
  };

  return (
    <Layout style={{ height: '100vh' }}>
      <Header
        style={{
          background: '#fff',
          borderBottom: '1px solid #f0f0f0',
          padding: '0 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack} size='large'>
          {t('back')}
        </Button>

        <Title level={3} style={{ margin: 0 }}>
          {isEditMode ? t('edit_test') || 'Editar Teste' : t('create_test') || 'Criar Teste'}
        </Title>

        <Button
          type='primary'
          icon={<SaveOutlined />}
          loading={loading}
          onClick={handleSubmit(onSubmit)}
          size='large'
        >
          {isEditMode ? t('save') || 'Salvar' : t('create') || 'Criar'}
        </Button>
      </Header>

      <Content
        style={{
          overflowY: 'auto',
          background: '#f5f5f5',
          padding: '16px 24px',
        }}
      >
        <div style={{ maxWidth: 1400, margin: '0 auto', paddingBottom: 100 }}>
          <Card style={{ marginBottom: 20 }}>
            <Title level={4}>Informações Básicas</Title>

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Form.Item
                  label='Título do Teste'
                  validateStatus={errors.title ? 'error' : ''}
                  help={errors.title?.message}
                  required
                >
                  <Controller
                    name='title'
                    control={control}
                    render={({ field }) => (
                      <Input {...field} placeholder='Digite o título do teste' />
                    )}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label='Código'
                  validateStatus={errors.code ? 'error' : ''}
                  help={errors.code?.message}
                  required
                >
                  <Controller
                    name='code'
                    control={control}
                    render={({ field }) => <Input {...field} placeholder='Ex: RTCB' />}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Form.Item
                  label='Nome do Relatório'
                  validateStatus={errors.reportName ? 'error' : ''}
                  help={errors.reportName?.message}
                  required
                >
                  <Controller
                    name='reportName'
                    control={control}
                    render={({ field }) => (
                      <Input {...field} placeholder='Digite o nome do relatório' />
                    )}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Form.Item
                  label='Plataforma'
                  validateStatus={errors.platform ? 'error' : ''}
                  help={errors.platform?.message}
                  required
                >
                  <Controller
                    name='platform'
                    control={control}
                    render={({ field }) => (
                      <Select {...field} placeholder='Selecione a plataforma'>
                        <Option value='mobile'>Mobile</Option>
                        <Option value='desktop'>Desktop</Option>
                      </Select>
                    )}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label='Tentativas Permitidas'
                  validateStatus={errors.allowedAttempts ? 'error' : ''}
                  help={errors.allowedAttempts?.message}
                  required
                >
                  <Controller
                    name='allowedAttempts'
                    control={control}
                    render={({ field }) => (
                      <InputNumber {...field} min={1} style={{ width: '100%' }} />
                    )}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Form.Item
                  label='Categoria do Asset'
                  validateStatus={errors.assetCategory ? 'error' : ''}
                  help={errors.assetCategory?.message}
                  required
                >
                  <Controller
                    name='assetCategory'
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        mode='multiple'
                        placeholder={
                          categoriesLoading ? 'Carregando categorias...' : 'Selecione as categorias'
                        }
                        loading={categoriesLoading}
                        disabled={categoriesLoading}
                      >
                        {mockData.assetCategories.map((item) => (
                          <Option key={item.value} value={item.value}>
                            {item.label}
                          </Option>
                        ))}
                      </Select>
                    )}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Form.Item
                  label='Instrumentos'
                  validateStatus={errors.instrumentIds ? 'error' : ''}
                  help={errors.instrumentIds?.message}
                  required
                >
                  <Controller
                    name='instrumentIds'
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        mode='multiple'
                        placeholder={instrumentsLoading ? 'Carregando instrumentos...' : 'Selecione os instrumentos'}
                        loading={instrumentsLoading}
                        disabled={instrumentsLoading}
                        style={{ width: '100%' }}
                      >
                        {mockData.instruments.map((item) => (
                          <Option key={item.value} value={item.value}>
                            {item.label}
                          </Option>
                        ))}
                      </Select>
                    )}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label='Propriedades do Asset para Exibição'
                  validateStatus={errors.assetDisplayProperties ? 'error' : ''}
                  help={
                    errors.assetDisplayProperties?.message ||
                    (!watchedAssetCategory || watchedAssetCategory.length === 0
                      ? 'Selecione primeiro uma categoria de asset'
                      : '')
                  }
                  required
                >
                  <Controller
                    name='assetDisplayProperties'
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        mode='multiple'
                        placeholder={
                          !watchedAssetCategory || watchedAssetCategory.length === 0
                            ? 'Selecione primeiro uma categoria de asset'
                            : 'Selecione as propriedades'
                        }
                        style={{ width: '100%' }}
                        disabled={
                          !watchedAssetCategory ||
                          watchedAssetCategory.length === 0 ||
                          availableProperties.length === 0
                        }
                      >
                        {availableProperties.map((item) => (
                          <Option key={item.key} value={item.key}>
                            {item.label}
                          </Option>
                        ))}
                      </Select>
                    )}
                  />
                </Form.Item>
              </Col>
            </Row>


            <Row gutter={[16, 16]}>
              <Col span={24}>
                <div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 12,
                    }}
                  >
                    <Text strong>Anotações:</Text>
                    <Button
                      type='dashed'
                      size='small'
                      icon={<PlusOutlined />}
                      onClick={addAnnotation}
                    >
                      Adicionar Anotação
                    </Button>
                  </div>

                  {watch('annotations')?.map((annotation, annotationIndex) => (
                    <div key={annotationIndex} style={{ display: 'flex', marginBottom: 12 }}>
                      <Controller
                        name={`annotations.${annotationIndex}`}
                        control={control}
                        render={({ field }) => (
                          <Input
                            {...field}
                            placeholder='Digite uma anotação'
                            style={{ marginRight: 8, height: '32px' }}
                            size='small'
                          />
                        )}
                      />
                      {watch('annotations').length > 1 && (
                        <Button
                          type='text'
                          danger
                          size='small'
                          onClick={() => removeAnnotation(annotationIndex)}
                          style={{ height: '32px' }}
                        >
                          Remover
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
          </Card>

          <Card style={{ opacity: basicInfoCompleted ? 1 : 0.6 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 16,
              }}
            >
              <Title level={4}>
                Steps do Teste
                {!basicInfoCompleted && (
                  <span
                    style={{
                      color: '#ff4d4f',
                      fontSize: '14px',
                      fontWeight: 'normal',
                      marginLeft: 8,
                    }}
                  >
                    (Complete as informações básicas primeiro)
                  </span>
                )}
              </Title>
              <Tooltip
                title={!basicInfoCompleted ? 'Complete as informações básicas primeiro' : ''}
              >
                <Button
                  type='dashed'
                  icon={<PlusOutlined />}
                  onClick={addStep}
                  disabled={!basicInfoCompleted}
                >
                  Adicionar Step
                </Button>
              </Tooltip>
            </div>

            <div
              style={{
                opacity: basicInfoCompleted ? 1 : 0.5,
                pointerEvents: basicInfoCompleted ? 'auto' : 'none',
              }}
            >
              {watchedSteps.map((step, index) => (
                <Card
                  key={index}
                  type='inner'
                  style={{ marginBottom: 16 }}
                  extra={
                    watchedSteps.length > 1 && (
                      <Button type='text' danger onClick={() => removeStep(index)}>
                        Remover
                      </Button>
                    )
                  }
                >
                  <StepConfigFields
                    stepIndex={index}
                    control={control}
                    errors={errors}
                    mockData={mockData}
                    setValue={setValue}
                  />

                  {/* Campos específicos para câmera e evidência */}
                  {(step.stepType === 'camera' || step.stepType === 'evidence') && (
                    <Row gutter={[16, 16]}>
                      <Col span={12}>
                        <Form.Item
                          label='Duração (segundos)'
                          validateStatus={errors.steps?.[index]?.durationInSeconds ? 'error' : ''}
                          help={errors.steps?.[index]?.durationInSeconds?.message}
                          required
                        >
                          <Controller
                            name={`steps.${index}.durationInSeconds`}
                            control={control}
                            render={({ field }) => (
                              <InputNumber
                                {...field}
                                min={1}
                                style={{ width: '100%' }}
                                placeholder='Digite a duração'
                              />
                            )}
                          />
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item
                          label='Intervalo (segundos)'
                          validateStatus={errors.steps?.[index]?.intervalInSeconds ? 'error' : ''}
                          help={errors.steps?.[index]?.intervalInSeconds?.message}
                          required
                        >
                          <Controller
                            name={`steps.${index}.intervalInSeconds`}
                            control={control}
                            render={({ field }) => (
                              <InputNumber
                                {...field}
                                min={1}
                                style={{ width: '100%' }}
                                placeholder='Digite o intervalo'
                              />
                            )}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}

                  {/* Seção para step do tipo tabela */}
                  {step.stepType === 'table' && (
                    <div style={{ marginTop: 16 }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: 12,
                        }}
                      >
                        <Title level={5}>Configuração da Tabela/Matriz</Title>
                        <Space>
                          <Tooltip title='Adicionar nova coluna'>
                            <Button
                              type='dashed'
                              icon={<PlusOutlined />}
                              onClick={() => {
                                const currentSteps = getValues('steps');
                                const updatedSteps = [...currentSteps];
                                if (!updatedSteps[index].tableConfig) {
                                  updatedSteps[index].tableConfig = { rows: [], columns: [] };
                                }
                                updatedSteps[index].tableConfig.columns.push(
                                  `Coluna ${updatedSteps[index].tableConfig.columns.length + 1}`,
                                );
                                setValue('steps', updatedSteps);
                              }}
                            >
                              Adicionar Coluna
                            </Button>
                          </Tooltip>
                        </Space>
                      </div>

                      {/* Tabela Principal */}
                      {(() => {
                        const tableData = [];
                        const columns = [
                          {
                            title: (
                              <div style={{ fontWeight: 'bold', textAlign: 'center' }}>
                                <Controller
                                  name={`steps.${index}.tableConfig.rowLabel`}
                                  control={control}
                                  render={({ field }) => (
                                    <Input
                                      {...field}
                                      placeholder='Ex: Equipamento'
                                      style={{
                                        fontWeight: 'bold',
                                        textAlign: 'center',
                                        width: '100%',
                                      }}
                                      size='small'
                                    />
                                  )}
                                />
                              </div>
                            ),
                            dataIndex: 'rowHeader',
                            key: 'rowHeader',
                            width: 200,
                            fixed: 'left',
                            render: (text, record, rowIndex) => (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Controller
                                  name={`steps.${index}.tableConfig.rows.${rowIndex}`}
                                  control={control}
                                  render={({ field }) => (
                                    <Input
                                      {...field}
                                      placeholder={`Item ${rowIndex + 1}`}
                                      style={{ fontWeight: 'bold' }}
                                      size='small'
                                    />
                                  )}
                                />
                                {step.tableConfig?.rows?.length > 1 && (
                                  <Button
                                    type='text'
                                    danger
                                    size='small'
                                    onClick={() => {
                                      const currentSteps = getValues('steps');
                                      const updatedSteps = [...currentSteps];
                                      updatedSteps[index].tableConfig.rows.splice(rowIndex, 1);
                                      setValue('steps', updatedSteps);
                                    }}
                                  >
                                    ×
                                  </Button>
                                )}
                              </div>
                            ),
                          },
                        ];

                        // Adicionar colunas dinamicamente
                        step.tableConfig?.columns?.forEach((column, colIndex) => {
                          columns.push({
                            title: (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Controller
                                  name={`steps.${index}.tableConfig.columns.${colIndex}`}
                                  control={control}
                                  render={({ field }) => (
                                    <Input
                                      {...field}
                                      placeholder={`Coluna ${colIndex + 1}`}
                                      style={{ fontWeight: 'bold', textAlign: 'center' }}
                                      size='small'
                                    />
                                  )}
                                />
                                {step.tableConfig?.columns?.length > 1 && (
                                  <Button
                                    type='text'
                                    danger
                                    size='small'
                                    onClick={() => {
                                      const currentSteps = getValues('steps');
                                      const updatedSteps = [...currentSteps];
                                      updatedSteps[index].tableConfig.columns.splice(colIndex, 1);
                                      setValue('steps', updatedSteps);
                                    }}
                                  >
                                    ×
                                  </Button>
                                )}
                              </div>
                            ),
                            dataIndex: `col_${colIndex}`,
                            key: `col_${colIndex}`,
                            width: 150,
                            render: () => (
                              <div
                                style={{
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: '#ccc',
                                  fontSize: '12px',
                                }}
                              >
                                [Campo]
                              </div>
                            ),
                          });
                        });

                        // Criar dados da tabela
                        step.tableConfig?.rows?.forEach((row, rowIndex) => {
                          const rowData = { key: rowIndex, rowHeader: row };
                          step.tableConfig?.columns?.forEach((col, colIndex) => {
                            rowData[`col_${colIndex}`] = '';
                          });
                          tableData.push(rowData);
                        });

                        return (
                          <div>
                            <Table
                              columns={columns}
                              dataSource={tableData}
                              pagination={false}
                              size='small'
                              bordered
                              scroll={{ x: 'max-content' }}
                              style={{ marginBottom: 16 }}
                            />

                            <div style={{ textAlign: 'center' }}>
                              <Tooltip title='Adicionar nova linha'>
                                <Button
                                  type='dashed'
                                  icon={<PlusOutlined />}
                                  onClick={() => {
                                    const currentSteps = getValues('steps');
                                    const updatedSteps = [...currentSteps];
                                    if (!updatedSteps[index].tableConfig) {
                                      updatedSteps[index].tableConfig = { rows: [], columns: [] };
                                    }
                                    updatedSteps[index].tableConfig.rows.push(
                                      `Linha ${updatedSteps[index].tableConfig.rows.length + 1}`,
                                    );
                                    setValue('steps', updatedSteps);
                                  }}
                                  block
                                >
                                  Adicionar Linha
                                </Button>
                              </Tooltip>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Estado vazio */}
                      {(!step.tableConfig?.rows?.length || !step.tableConfig?.columns?.length) && (
                        <div
                          style={{
                            textAlign: 'center',
                            padding: '40px',
                            color: '#999',
                            border: '2px dashed #d9d9d9',
                            borderRadius: '8px',
                            marginBottom: 16,
                          }}
                        >
                          <Text style={{ fontSize: '16px', display: 'block', marginBottom: 8 }}>
                            Configure sua tabela/matriz
                          </Text>
                          <Text style={{ fontSize: '14px', color: '#666' }}>
                            Use os botões "Adicionar Coluna" e "Adicionar Linha" para começar
                          </Text>
                          <br />
                          <Text style={{ color: 'red', fontSize: '12px' }}>
                            * Obrigatório pelo menos 1 linha e 1 coluna
                          </Text>
                        </div>
                      )}

                      {errors.steps?.[index]?.tableConfig && (
                        <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: 8 }}>
                          Steps de tabela devem ter pelo menos 1 linha e 1 coluna
                        </div>
                      )}
                    </div>
                  )}

                  {/* Campos para step do tipo formulário */}
                  {step.stepType === 'form' && (
                    <div style={{ marginTop: 16 }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: 12,
                        }}
                      >
                        <Title level={5}>Campos do Formulário</Title>
                        <Button
                          type='dashed'
                          size='middle'
                          icon={<PlusOutlined />}
                          onClick={() => addField(index)}
                        >
                          Adicionar Campo
                        </Button>
                      </div>

                      {step.fields?.length === 0 && (
                        <div
                          style={{
                            textAlign: 'center',
                            padding: '20px',
                            color: '#999',
                            border: '1px dashed #d9d9d9',
                            borderRadius: '4px',
                            marginBottom: '8px',
                          }}
                        >
                          <Text>
                            Nenhum campo adicionado. Clique em "Adicionar Campo" para começar.
                          </Text>
                          <Text style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                            * Obrigatório pelo menos 1 campo
                          </Text>
                        </div>
                      )}

                      {errors.steps?.[index]?.fields && (
                        <div style={{ color: '#ff4d4f', fontSize: '12px', marginBottom: '8px' }}>
                          Steps de formulário devem ter pelo menos 1 campo
                        </div>
                      )}

                      {step.fields?.map((field, fieldIndex) => (
                        <Card
                          key={fieldIndex}
                          size='small'
                          style={{ marginBottom: 8 }}
                          extra={
                            step.fields.length > 1 && (
                              <Button
                                type='text'
                                danger
                                size='small'
                                onClick={() => removeField(index, fieldIndex)}
                              >
                                Remover
                              </Button>
                            )
                          }
                        >
                          <Row gutter={[8, 8]}>
                            <Col span={12}>
                              <Form.Item
                                label='Label'
                                size='small'
                                validateStatus={
                                  errors.steps?.[index]?.fields?.[fieldIndex]?.label ? 'error' : ''
                                }
                                help={errors.steps?.[index]?.fields?.[fieldIndex]?.label?.message}
                                required
                              >
                                <Controller
                                  name={`steps.${index}.fields.${fieldIndex}.label`}
                                  control={control}
                                  render={({ field }) => (
                                    <Input {...field} placeholder='Label do campo' />
                                  )}
                                />
                              </Form.Item>
                            </Col>

                            <Col span={4}>
                              <Form.Item
                                label='Tipo'
                                size='small'
                                validateStatus={
                                  errors.steps?.[index]?.fields?.[fieldIndex]?.type ? 'error' : ''
                                }
                                help={errors.steps?.[index]?.fields?.[fieldIndex]?.type?.message}
                                required
                              >
                                <Controller
                                  name={`steps.${index}.fields.${fieldIndex}.type`}
                                  control={control}
                                  render={({ field }) => (
                                    <Select
                                      {...field}
                                      style={{ height: '32px' }}
                                      placeholder='Tipo'
                                      onChange={(value) => {
                                        field.onChange(value);
                                        // Quando trocar para select, garantir que tenha pelo menos 1 opção
                                        if (value === 'select') {
                                          const currentSteps = control._formValues.steps || [];
                                          const currentField =
                                            currentSteps[index]?.fields?.[fieldIndex];
                                          if (
                                            !currentField?.options ||
                                            currentField.options.length === 0
                                          ) {
                                            setValue(
                                              `steps.${index}.fields.${fieldIndex}.options`,
                                              ['Opção 1'],
                                            );
                                          }
                                        }
                                      }}
                                    >
                                      {mockData.fieldTypes.map((type) => (
                                        <Option key={type.value} value={type.value}>
                                          {type.label}
                                        </Option>
                                      ))}
                                    </Select>
                                  )}
                                />
                              </Form.Item>
                            </Col>

                            <VariantField
                              stepIndex={index}
                              fieldIndex={fieldIndex}
                              control={control}
                              errors={errors}
                              mockData={mockData}
                            />

                            <FieldControls
                              stepIndex={index}
                              fieldIndex={fieldIndex}
                              control={control}
                            />
                          </Row>

                          {/* Opções para campos do tipo select */}
                          {field.type === 'select' && (
                            <div style={{ marginTop: 8 }}>
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  marginBottom: 8,
                                }}
                              >
                                <div>
                                  <Text strong>
                                    Opções: <span style={{ color: 'red' }}>*</span>
                                  </Text>
                                  <div style={{ fontSize: '12px', color: '#666', marginTop: 2 }}>
                                    {field.typeVariant === 'single'
                                      ? 'Marque 1 opção como padrão'
                                      : 'Marque as opções corretas (múltiplas opções permitidas)'}
                                  </div>
                                </div>
                                <Button
                                  type='dashed'
                                  size='middle'
                                  icon={<PlusOutlined />}
                                  onClick={() => openOptionTypeModal(index, fieldIndex)}
                                >
                                  Adicionar Opção
                                </Button>
                              </div>

                              {errors.steps?.[index]?.fields?.[fieldIndex]?.options && (
                                <div
                                  style={{ color: '#ff4d4f', fontSize: '12px', marginBottom: 8 }}
                                >
                                  {errors.steps[index].fields[fieldIndex].options.message}
                                </div>
                              )}

                              {field.options?.map((option, optionIndex) => (
                                <OptionField
                                  key={optionIndex}
                                  stepIndex={index}
                                  fieldIndex={fieldIndex}
                                  optionIndex={optionIndex}
                                  option={option}
                                  field={field}
                                  control={control}
                                  canRemove={field.options.length > 1}
                                  onRemove={removeOption}
                                />
                              ))}
                            </div>
                          )}
                        </Card>
                      ))}
                    </div>
                  )}

                  {/* Campos para step do tipo câmera e evidência */}
                  {(step.stepType === 'camera' || step.stepType === 'evidence') && (
                    <div style={{ marginTop: 16 }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: 12,
                        }}
                      >
                        <Title level={5}>
                          Sub Steps ({step.stepType === 'camera' ? 'Câmera' : 'Evidência'})
                        </Title>
                        <Button
                          type='dashed'
                          size='small'
                          icon={<PlusOutlined />}
                          onClick={() => addSubStep(index)}
                        >
                          Adicionar Sub Step
                        </Button>
                      </div>

                      {step.subSteps?.length === 0 && (
                        <div
                          style={{
                            textAlign: 'center',
                            padding: '20px',
                            color: '#999',
                            border: '1px dashed #d9d9d9',
                            borderRadius: '4px',
                            marginBottom: '8px',
                          }}
                        >
                          <Text>
                            Nenhum sub step adicionado. Clique em "Adicionar Sub Step" para começar.
                          </Text>
                          <Text style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                            * Obrigatório pelo menos 1 sub step
                          </Text>
                        </div>
                      )}

                      {errors.steps?.[index]?.subSteps && (
                        <div style={{ color: '#ff4d4f', fontSize: '12px', marginBottom: '8px' }}>
                          Steps de câmera devem ter pelo menos 1 sub step
                        </div>
                      )}

                      {step.subSteps?.map((subStep, subStepIndex) => (
                        <Card
                          key={subStepIndex}
                          size='small'
                          style={{ marginBottom: 8 }}
                          extra={
                            step.subSteps.length > 1 && (
                              <Button
                                type='text'
                                danger
                                size='small'
                                onClick={() => removeSubStep(index, subStepIndex)}
                              >
                                Remover
                              </Button>
                            )
                          }
                        >
                          <Row gutter={[8, 8]}>
                            <Col span={12}>
                              <Form.Item
                                label='Título do Sub Step'
                                size='small'
                                validateStatus={
                                  errors.steps?.[index]?.subSteps?.[subStepIndex]?.title
                                    ? 'error'
                                    : ''
                                }
                                help={
                                  errors.steps?.[index]?.subSteps?.[subStepIndex]?.title?.message
                                }
                                required
                              >
                                <Controller
                                  name={`steps.${index}.subSteps.${subStepIndex}.title`}
                                  control={control}
                                  render={({ field }) => (
                                    <Input {...field} placeholder='Título do sub step' />
                                  )}
                                />
                              </Form.Item>
                            </Col>

                            {step.stepType === 'camera' && (
                              <Col span={12}>
                                <Form.Item
                                  label='Instrumentos'
                                  size='small'
                                  validateStatus={
                                    errors.steps?.[index]?.subSteps?.[subStepIndex]?.instrumentId
                                      ? 'error'
                                      : ''
                                  }
                                  help={
                                    errors.steps?.[index]?.subSteps?.[subStepIndex]?.instrumentId
                                      ?.message
                                  }
                                  required
                                >
                                  <Controller
                                    name={`steps.${index}.subSteps.${subStepIndex}.instrumentId`}
                                    control={control}
                                    render={({ field }) => (
                                      <Select
                                        {...field}
                                        mode='multiple'
                                        placeholder={instrumentsLoading ? 'Carregando instrumentos...' : 'Selecione os instrumentos'}
                        loading={instrumentsLoading}
                        disabled={instrumentsLoading}
                                        style={{ minHeight: '32px' }}
                                      >
                                        {mockData.instruments.map((instrument) => (
                                          <Option key={instrument.value} value={instrument.value}>
                                            {instrument.label}
                                          </Option>
                                        ))}
                                      </Select>
                                    )}
                                  />
                                </Form.Item>
                              </Col>
                            )}
                          </Row>

                          {step.stepType === 'camera' && (
                            <Row gutter={[8, 8]} style={{ marginTop: 16 }}>
                              <Col span={12}>
                                <Form.Item label='Deve Validar' size='small'>
                                  <Controller
                                    name={`steps.${index}.subSteps.${subStepIndex}.shouldValidate`}
                                    control={control}
                                    render={({ field }) => (
                                      <Switch {...field} checked={field.value} size='small' />
                                    )}
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                          )}

                          <div style={{ marginTop: 16 }}>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: 12,
                              }}
                            >
                              <Text strong>
                                Hints: <span style={{ color: 'red' }}>*</span>
                              </Text>
                              <Button
                                type='dashed'
                                size='small'
                                icon={<PlusOutlined />}
                                onClick={() => addHint(index, subStepIndex)}
                              >
                                Adicionar Hint
                              </Button>
                            </div>

                            {errors.steps?.[index]?.subSteps?.[subStepIndex]?.hints && (
                              <div style={{ color: '#ff4d4f', fontSize: '12px', marginBottom: 12 }}>
                                {errors.steps[index].subSteps[subStepIndex].hints.message}
                              </div>
                            )}

                            {subStep.hints?.map((hint, hintIndex) => (
                              <div key={hintIndex} style={{ display: 'flex', marginBottom: 12 }}>
                                <Controller
                                  name={`steps.${index}.subSteps.${subStepIndex}.hints.${hintIndex}`}
                                  control={control}
                                  render={({ field }) => (
                                    <Input
                                      {...field}
                                      placeholder='Digite o hint'
                                      style={{ marginRight: 8, height: '32px' }}
                                      size='small'
                                    />
                                  )}
                                />
                                {subStep.hints.length > 1 && (
                                  <Button
                                    type='text'
                                    danger
                                    size='small'
                                    onClick={() => removeHint(index, subStepIndex, hintIndex)}
                                    style={{ height: '32px' }}
                                  >
                                    Remover
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </Card>
              ))}
            </div>

            {errors.steps && <Text type='danger'>{errors.steps.message}</Text>}
          </Card>
        </div>
      </Content>

      {/* Modal componentizado para seleção do tipo de opção */}
      <AddOptionModal
        visible={optionTypeModal.visible}
        onCancel={() => setOptionTypeModal({ visible: false, stepIndex: null, fieldIndex: null })}
        onAddOption={addOptionByType}
        assetDisplayProperties={availableProperties}
      />
    </Layout>
  );
};

export default CreateTestForm;
